import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

function detectRuntimeSerialization(): boolean {
  let stackGetterCalls = 0;
  const error = new Error();
  Object.defineProperty(error, 'stack', {
    configurable: false,
    enumerable: false,
    get(): string {
      stackGetterCalls += 1;
      return '';
    },
  });
  console.debug(error);
  return stackGetterCalls > 0;
}

export async function detectOpenDevTools(): Promise<boolean> {
  const source = `
self.onmessage = function () {
  self.postMessage('before');
  debugger;
  self.postMessage('after');
};
`;
  const url = URL.createObjectURL(
    new Blob([source], { type: 'text/javascript' }),
  );
  return await new Promise<boolean>((resolve, reject) => {
    const worker = new Worker(url);
    let before: number | null = null;
    let detectionTimeout: number | null = null;
    let finished = false;
    const startupTimeout = window.setTimeout(() => {
      finish(new Error('DevTools detection Worker timed out'));
    }, 2000);
    const finish = (result: boolean | Error): void => {
      if (finished) {
        return;
      }
      finished = true;
      window.clearTimeout(startupTimeout);
      if (detectionTimeout != null) {
        window.clearTimeout(detectionTimeout);
      }
      worker.terminate();
      URL.revokeObjectURL(url);
      if (result instanceof Error) {
        reject(result);
      } else {
        resolve(result);
      }
    };
    worker.addEventListener('message', (event: MessageEvent<unknown>) => {
      if (event.data == 'before') {
        before = performance.now();
        detectionTimeout = window.setTimeout(() => finish(true), 500);
        return;
      }
      if (event.data == 'after' && before != null) {
        finish(performance.now() - before > 100);
      }
    });
    worker.addEventListener('error', (event) => {
      finish(new Error(event.message || 'DevTools detection Worker failed'));
    });
    worker.postMessage('start');
  });
}

async function collect(): Promise<RawData> {
  return {
    webdriver: navigator.webdriver,
    runtimeSerializationDetected: detectRuntimeSerialization(),
    devToolsDetected: await detectOpenDevTools(),
  };
}

function validateRawCdp(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  if (rawData.webdriver !== false) {
    reasons.push('navigator.webdriver must be false');
  }
  if (rawData.runtimeSerializationDetected !== false) {
    reasons.push('CDP Runtime is enabled and serialized Error.stack');
  }
  if (rawData.devToolsDetected !== false) {
    reasons.push('An open DevTools session paused the detection Worker');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return rawData.runtimeSerializationDetected == false &&
    rawData.devToolsDetected == false
    ? 'CDP disabled'
    : 'CDP detected';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateRawCdp(rawData);
}

export const rawCdpVisibilityTest: TestDefinition = {
  id: 'rawCdpVisibility',
  title: 'Raw CDP visibility',
  category: 'shared',
  format,
  run,
  validate,
};
